/**
 * Comments of one customer (note / action / decision). Hosts the unchanged `CreditLimitComments`; who may write is
 * decided by the caller (`canWriteComments`, E21), the server checks it again.
 */
import { useCallback, useEffect, useState } from 'react';
import { creditLimitsApi } from '../../../services/creditLimitsApi';
import type { CreditLimitComment, OverviewRow } from '../../../types/creditLimits';
import { CreditLimitComments } from '../CreditLimitComments';
import { OverviewDialog } from './OverviewDialog';

interface Props {
    row: OverviewRow;
    canEdit: boolean;
    onClose: () => void;
}

export function CommentsDialog({ row, canEdit, onClose }: Props) {
    const [comments, setComments] = useState<CreditLimitComment[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        creditLimitsApi.getComments(row.customerCode)
            .then(c => { setComments(c); setError(null); })
            .catch(err => setError(err instanceof Error ? err.message : 'Failed to load comments'));
    }, [row.customerCode]);

    useEffect(() => { load(); }, [load]);

    return (
        <OverviewDialog title="Comments" subtitle={`${row.customerName ?? row.customerCode} (${row.customerCode})`} onClose={onClose} width={640}>
            {error && <div className="banner error" role="alert"><p>{error}</p></div>}
            {!error && !comments && <p className="muted" role="status">Loading…</p>}
            {!canEdit && comments && (
                <p className="muted">You can read the comments. Only finance, the salesperson and the BT manager of this customer can add or change them.</p>
            )}
            {comments && (
                <CreditLimitComments customerCode={row.customerCode} comments={comments} canEdit={canEdit} onCommentChanged={load} />
            )}
        </OverviewDialog>
    );
}
